import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  financeService,
  type FinanceAssignee,
  type FinanceRequestDetail,
} from '@/services/financeService'
import { useAuthStore } from '@/stores/authStore'
import { canViewFinanceFinancialValues, canViewFinanceClosedScope } from '@/components/Financeiro/financeGestorVisibility'

const QUEUE_ROLES = ['admin', 'financeiro', 'manager']
const G5_INVITE_ROLES = ['admin', 'financeiro']

export function useFinanceRequestDetailPage() {
  const { requestId } = useParams<{ requestId: string }>()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isGestoresContext = location.pathname.startsWith('/gestores/chamados/')
  const userRole = (useAuthStore((s) => s.user?.role) ?? '').toLowerCase()
  const canManageQueue = QUEUE_ROLES.includes(userRole)
  const canGenerateG5Invite = G5_INVITE_ROLES.includes(userRole)
  const canViewFinancialValues = canViewFinanceFinancialValues(userRole)
  const canViewClosedScope = canViewFinanceClosedScope(userRole)

  const [detail, setDetail] = useState<FinanceRequestDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [assignees, setAssignees] = useState<FinanceAssignee[]>([])

  const load = useCallback(async () => {
    if (!requestId) return
    setLoading(true)
    setError(null)
    try {
      const data = await financeService.getRequest(requestId)
      setDetail(data)
    } catch {
      setError('Não foi possível carregar a solicitação.')
      setDetail(null)
    } finally {
      setLoading(false)
    }
  }, [requestId])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!canManageQueue) return
    void financeService.listAssignees().then(setAssignees).catch(() => setAssignees([]))
  }, [canManageQueue])

  const goBack = useCallback(() => {
    if (isGestoresContext) {
      const back = searchParams.get('back')
      navigate(`/gestores${back ? `?${back}` : '?tab=chamados'}`)
      return
    }
    navigate('/financeiro?tab=chamados')
  }, [isGestoresContext, searchParams, navigate])

  const updateStatus = useCallback(
    async (status: string, approvalPendingFrom?: string) => {
      if (!requestId) return false
      try {
        const updated = await financeService.updateRequestStatus(
          requestId,
          status,
          approvalPendingFrom,
        )
        setDetail((prev) =>
          prev
            ? {
                ...prev,
                status: updated.status,
                approvalPendingFrom: updated.approvalPendingFrom ?? null,
              }
            : prev,
        )
        toast.success('Status atualizado')
        return true
      } catch {
        toast.error('Falha ao atualizar status')
        return false
      }
    },
    [requestId],
  )

  const assignRequest = useCallback(
    async (assigneeId: string | null) => {
      if (!requestId) return false
      try {
        const updated = await financeService.assignRequest(requestId, assigneeId)
        setDetail((prev) =>
          prev ? { ...prev, assignee: updated.assignee ?? null } : prev,
        )
        toast.success('Responsável atualizado')
        return true
      } catch {
        toast.error('Falha ao atribuir responsável')
        return false
      }
    },
    [requestId],
  )

  const addComment = useCallback(
    async (body: string, isInternal = false, attachments: string[] = []) => {
      if (!requestId) return false
      try {
        const { comment, requestStatus } = await financeService.addRequestComment(
          requestId,
          body,
          isInternal,
          attachments,
        )
        setDetail((prev) =>
          prev
            ? {
                ...prev,
                comments: [...prev.comments, comment],
                ...(requestStatus ? { status: requestStatus } : {}),
              }
            : prev,
        )
        return true
      } catch {
        toast.error('Falha ao enviar comentário')
        return false
      }
    },
    [requestId],
  )

  return {
    requestId,
    detail,
    loading,
    error,
    canManageQueue,
    canGenerateG5Invite,
    canViewFinancialValues,
    canViewClosedScope,
    assignees,
    reload: load,
    goBack,
    updateStatus,
    assignRequest,
    addComment,
    isGestoresContext,
  }
}
